/**
 * Generates exciting, catchy intros for match feed cards.
 * Mix of hype, mystery (who will win?), and support/poll prompts.
 */

// Simple hash for deterministic template selection
function hash(str) {
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    h = ((h << 5) - h) + str.charCodeAt(i) | 0;
  }
  return Math.abs(h);
}

function pick(seed, arr) {
  return arr[hash(String(seed)) % arr.length];
}

/** Intros for LIVE / UPCOMING matches – build hype, guessing, support */
const LIVE_INTROS = [
  (p1, p2) => `${p1} and ${p2} are about to clash. Who's taking it? 🤔`,
  (p1, p2) => `The table is set. ${p1} vs ${p2} – who do you think will win?`,
  (p1, p2) => `It's go time! ${p1} squares off against ${p2}. Place your bets below 👇`,
  (p1, p2) => `Showdown incoming: ${p1} vs ${p2}. Who's got your support?`,
  (p1, p2) => `${p1} vs ${p2} – the tension is real. Cast your vote!`,
  (p1, p2) => `All eyes on this one. ${p1} vs ${p2}. Who will triumph?`,
  (p1, p2) => `This match could go either way. ${p1} vs ${p2} – who are you rooting for?`,
  (p1, p2) => `${p1} and ${p2} face off. Your prediction? Vote below!`,
  (p1, p2) => `Ping pong fever: ${p1} vs ${p2}. Who's got this? 🏓`,
  (p1, p2) => `The battle begins. ${p1} vs ${p2} – show your support!`,
  (p1, p2) => `Only one can win. ${p1} vs ${p2}. Who do you think it'll be?`,
  (p1, p2) => `No love lost here. ${p1} vs ${p2}. Who are you backing?`,
  (p1, p2) => `The stakes are high. ${p1} vs ${p2}. Place your vote! 👇`,
  (p1, p2) => `${p1} vs ${p2} – who survives? Vote for your pick!`,
  (p1, p2) => `Get your popcorn. ${p1} and ${p2} are going head-to-head. Who wins?`,
];

/** Intros for COMPLETED matches – celebrate the result */
const COMPLETED_INTROS = [
  (winner, p1, p2, scoreText) => `${winner} takes it! ${scoreText !== '—' ? scoreText : `${p1} vs ${p2}`}`,
  (winner, p1, p2, scoreText) => `${winner} wins! ${scoreText !== '—' ? `Final: ${scoreText}` : `vs ${p2}`}`,
  (winner, p1, p2, scoreText) => `Victory for ${winner}! ${scoreText !== '—' ? scoreText : ''}`,
  (winner, p1, p2, scoreText) => `${winner} comes out on top ${scoreText !== '—' ? `(${scoreText})` : ''}`,
  (winner, p1, p2, scoreText) => `And the winner is… ${winner}! ${scoreText !== '—' ? scoreText : ''}`,
  (winner, p1, p2, scoreText) => `${winner} seals the deal. ${scoreText !== '—' ? scoreText : `${p1} vs ${p2}`}`,
  (winner, p1, p2, scoreText) => `${winner} dominates! ${scoreText !== '—' ? scoreText : ''}`,
  (winner, p1, p2, scoreText) => `Champion: ${winner}. ${scoreText !== '—' ? scoreText : ''}`,
  (winner, p1, p2, scoreText) => `${winner} gets the W. ${scoreText !== '—' ? scoreText : ''}`,
  (winner, p1, p2, scoreText) => `${winner} wins the battle. ${scoreText !== '—' ? scoreText : ''}`,
];

/** Intros for COMPLETED DOUBLES – include win counts, team stats */
const COMPLETED_DOUBLES_INTROS = [
  (winner, p1, p2, scoreText, winA, winB) =>
    `${winner} wins! ${scoreText !== '—' ? scoreText : ''}${winA != null && winB != null ? ` (${formatDoublesWins(winA, winB)})` : ''}`,
  (winner, p1, p2, scoreText, winA, winB) =>
    `${winner} takes it! ${scoreText !== '—' ? scoreText : ''}${winA != null && winB != null ? ` Now at ${winA} & ${winB} wins.` : ''}`,
  (winner, p1, p2, scoreText, winA, winB) =>
    `Victory for ${winner}! ${scoreText !== '—' ? scoreText : ''}${winA != null && winB != null ? ` (${winA} + ${winB} wins combined)` : ''}`,
  (winner, p1, p2, scoreText, winA, winB) =>
    `${winner} comes out on top. ${scoreText !== '—' ? scoreText : ''}${winA != null && winB != null ? ` That's win #${winA} & #${winB} for the pair!` : ''}`,
  (winner, p1, p2, scoreText, winA, winB) =>
    `${winner} seals the deal! ${scoreText !== '—' ? scoreText : ''}${winA != null && winB != null ? ` ${winA}/${winB} career wins.` : ''}`,
  (winner, p1, p2, scoreText, winA, winB) =>
    `${winner} dominates! ${scoreText !== '—' ? scoreText : ''}${winA != null && winB != null ? ` Combined ${winA + winB} wins and counting.` : ''}`,
  (winner, p1, p2, scoreText, winA, winB) =>
    `And the winner is… ${winner}! ${scoreText !== '—' ? scoreText : ''}${winA != null && winB != null ? ` (${formatDoublesWins(winA, winB)})` : ''}`,
  (winner, p1, p2, scoreText, winA, winB) =>
    `${winner} gets the W. ${scoreText !== '—' ? scoreText : ''}${winA != null && winB != null ? ` Now ${winA} & ${winB} wins.` : ''}`,
];

function formatDoublesWins(w1, w2) {
  if (w1 === 1 && w2 === 1) return '1st win each!';
  if (w1 === 1 || w2 === 1) return `${Math.max(w1, w2)} wins for one, 1st for the other!`;
  return `${w1} & ${w2} wins`;
}

/** Generic intros for non-match-completed, non-started (scheduled, created, etc.) */
const GENERIC_INTROS = [
  (p1, p2) => `${p1} vs ${p2} – mark your calendars!`,
  (p1, p2) => `Upcoming: ${p1} vs ${p2}. Who will come out on top?`,
  (p1, p2) => `Match on the horizon: ${p1} vs ${p2}.`,
  (p1, p2) => `${p1} and ${p2} will face off. Stay tuned!`,
  (p1, p2) => `Get ready: ${p1} vs ${p2} coming up.`,
];

/** Poll/support callout lines – shown above reaction strip (live/upcoming) */
const POLL_CALLOUTS = [
  "Who's got this? Vote below! 👇",
  "Cast your vote! Who are you backing?",
  "Place your bet. Support your pick!",
  "Who do you think will win?",
  "Show your support! 👇",
  "Rooting for someone? Vote here!",
  "Your prediction? Vote below!",
  "Who are you cheering for?",
];

/** Support callout lines – for completed matches (show support for winner/loser) */
const SUPPORT_CALLOUTS = [
  "Show your support below! 👇",
  "Were you right? Back your pick!",
  "Who did you back? Support them!",
  "Reactions welcome! 👇",
  "Show some love below!",
  "Support your favorite! 👇",
];

/**
 * Generate an exciting intro for a live/upcoming match
 * @param {string} p1 - Player/team 1 name
 * @param {string} p2 - Player/team 2 name
 * @param {string} seed - MatchId or feedItemId for deterministic pick
 */
export function generateLiveIntro(p1, p2, seed) {
  const template = pick(seed, LIVE_INTROS);
  return template(p1, p2);
}

/**
 * Generate an exciting intro for a completed match
 * @param {string} winner - Winner name
 * @param {string} p1 - Player/team 1 name
 * @param {string} p2 - Player/team 2 name
 * @param {string} scoreText - Formatted score (e.g. "11-9, 11-7")
 * @param {string} seed - MatchId for deterministic pick
 * @param {object} options - Optional: { isDoubles, winnerWins: [w1, w2] }
 */
export function generateCompletedIntro(winner, p1, p2, scoreText, seed, options = {}) {
  const { isDoubles, winnerWins = [] } = options;
  const [winA, winB] = winnerWins;
  if (isDoubles && (winA != null || winB != null)) {
    const template = pick(seed, COMPLETED_DOUBLES_INTROS);
    return template(winner, p1, p2, scoreText || '—', winA, winB);
  }
  const template = pick(seed, COMPLETED_INTROS);
  return template(winner, p1, p2, scoreText || '—');
}

/**
 * Generate an intro for generic match updates (scheduled, created, etc.)
 */
export function generateGenericIntro(p1, p2, seed) {
  const template = pick(seed, GENERIC_INTROS);
  return template(p1, p2);
}

/**
 * Generate a poll/support callout for live matches
 */
export function generatePollCallout(seed) {
  return pick(seed, POLL_CALLOUTS);
}

/**
 * Generate a support callout for completed matches
 */
export function generateSupportCallout(seed) {
  return pick(seed, SUPPORT_CALLOUTS);
}
